import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { Alert, Label } from '@trussworks/react-uswds';

import StepHeader from '@components/step-header/StepHeader';
import DatePicker from '@components/date-picker/DatePicker';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import ExternalLink from '@components/external-link/ExternalLink';
import Tooltip from '@components/tooltip/tooltip';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Section from '@components/section/Section';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { useRequest, useIpac } from '@stores';
import { useResetDirtyFields } from '@hooks';

import { dateBeforeA } from '@src/utils/helpers';
import IPACSpeciesTable from './IPACSpeciesTable';
import { IPACSupportedFootprintGeometryType, PermitTypes, RequestTypeStringIds } from '@src/utils/enums';
import { useQuery } from '@tanstack/react-query';
import { IpacQueries } from '@api/services/ipac';

import {
  formatSpeciesList,
  getProjectLocationWkt,
  getSpeciesFootprint,
  gpSchema,
  ipSchema,
  otherLawsAndRegulationsText as text,
} from './otherLawsAndRegulationsHelpers';

export const PermitsFormOtherLawsRegulationsMetadata = {
  sectionName: 'Other Laws and Regulations',
};

const PermitsFormOtherLawsRegulations = connect(
  'doUpdatePermitRequest',
  'selectPermitData',
  'selectRequestAPIData',
  'selectRequestFormData',
  ({ doUpdatePermitRequest, permitData, requestAPIData, requestFormData, stepNo }) => {
    const {
      isFormReadOnly: isReadOnly,
      isSectionTouched,
      projectGeometry: savedProjectGeometry,
      selectedRequestId,
      updateSection,
      updateSectionProps,
    } = useRequest();
    const { setEndangeredSpeciesList, selectedSpecies, setSelectedSpecies } = useIpac();
    const prevRequestAPIDataRef = useRef();

    const isGpRequest =
      [RequestTypeStringIds.PreconstructionNotification, RequestTypeStringIds.NationwidePermit].includes(
        selectedRequestId
      ) || permitData?.requestedPermitType === PermitTypes.GeneralPermit;

    const [speciesData, setSpeciesData] = useState([]);
    const [speciesDataIsDirty, setSpeciesDataIsDirty] = useState(false);

    const schema = useMemo(
      () => (isGpRequest ? yup.object().shape(gpSchema) : yup.object().shape(ipSchema)),
      [isGpRequest]
    );

    const projectGeometry = useMemo(
      () =>
        Object.keys(savedProjectGeometry || {}).length ? savedProjectGeometry : requestFormData?.location?.geometry,
      [savedProjectGeometry, requestFormData?.location?.geometry]
    );

    const speciesFromDb = useMemo(
      () => permitData?.species?.map((species) => species.commonName),
      [permitData?.species]
    );

    const defaultValues = useMemo(
      () => ({
        affectedSpecies: permitData?.affectedSpecies ?? '',
        impactedHistoricalProperty: permitData?.impactedHistoricalProperty ?? '',
        isWildScenicRiver: permitData?.isWildScenicRiver ?? '',
        wildScenicRiverName: permitData?.wildScenicRiverName ?? '',
        isS408PermissionRequired: permitData?.isS408PermissionRequired ?? null,
        isS408RequestSubmitted:
          permitData?.isS408RequestSubmitted === true || permitData?.isS408RequestSubmitted === 'true'
            ? 'true'
            : permitData?.isS408RequestSubmitted === false || permitData?.isS408RequestSubmitted === 'false'
              ? 'false'
              : null,
        s408RequestSubmittedDate:
          permitData?.s408RequestSubmittedDate !== null ? permitData?.s408RequestSubmittedDate?.split('T')[0] : '',
      }),
      [permitData]
    );

    const requestData = useMemo(() => {
      if (Object.values(IPACSupportedFootprintGeometryType).includes(projectGeometry?.type)) {
        return { geoData: getSpeciesFootprint(projectGeometry), isFootprint: true };
      } else {
        return { geoData: getProjectLocationWkt(projectGeometry), isFootprint: false };
      }
    }, [projectGeometry]);

    const {
      data: endangeredSpeciesList,
      refetch: fetchSpecies,
      isFetching: isLoading,
      isError,
      isSuccess,
    } = useQuery({
      queryKey: ['ipacSpecies', requestData],
      enabled: !isReadOnly,
      retry: 0,
      queryFn: () => IpacQueries.getSpeciesList(requestData),
    });

    useEffect(() => {
      if (isSuccess) {
        setEndangeredSpeciesList(endangeredSpeciesList);
      } else {
        const species = requestAPIData?.request?.permits?.[0]?.species;
        if (species) {
          const formattedSpeciesList = formatSpeciesList(species);
          setEndangeredSpeciesList(formattedSpeciesList);
        }
      }
    }, [endangeredSpeciesList, isSuccess, requestAPIData?.request?.permits, setEndangeredSpeciesList]);

    useEffect(() => {
      speciesData && doUpdatePermitRequest({ species: speciesData });
    }, [speciesData, doUpdatePermitRequest]);

    const methods = useForm({
      resolver: yupResolver(schema),
      mode: 'onBlur',
      defaultValues: defaultValues,
      context: { selectedSpecies },
    });

    const {
      formState: { errors, dirtyFields },
      watch,
      trigger,
      reset,
    } = methods;

    const nationalRiver = watch('isWildScenicRiver');
    const civilWorkSelect = watch('isS408PermissionRequired');
    const submittedRequest = watch('isS408RequestSubmitted');

    const isDirty = Object.keys(dirtyFields).length > 0 || speciesDataIsDirty;
    const isValid = Object.keys(errors).length === 0;
    const isTouched = isSectionTouched(stepNo);

    const handleChange = useCallback(
      (event) => {
        const { name, value } = event.target;
        doUpdatePermitRequest({ [name]: value });
        trigger(name);
      },
      [doUpdatePermitRequest, trigger]
    );

    const handleSpeciesCheck = useCallback(
      (event) => {
        const { name, checked } = event.target;
        setSpeciesDataIsDirty(true);

        if (checked) {
          setSelectedSpecies((prev) => [...prev, name]);
          const speciesDataFromRequest = requestAPIData?.request?.permits?.[0]?.species || [];
          const addedSpeciesData = endangeredSpeciesList
            .filter((species) => species.optionalCommonName === name)
            .map((item) => {
              const speciesFromRequest = speciesDataFromRequest.find((species) => species.speciesID === item.speciesID);
              return {
                ipacSpeciesID: item.speciesId,
                commonName: speciesFromRequest ? speciesFromRequest.commonName : item.optionalCommonName,
                scientificName: item.optionalScientificName,
                listingStatus: item.listingStatusName,
                profileURL: item.speciesProfileUrl,
                isAffectedSpecies: true,
              };
            });
          setSpeciesData((prev) => [...prev, ...addedSpeciesData]);
        }

        if (!checked) {
          setSelectedSpecies((prev) => prev.filter((val) => val !== name));
          const removedSpeciesData = speciesData.filter((species) => species.commonName !== name);
          setSpeciesData(removedSpeciesData);
        }
      },
      [endangeredSpeciesList, requestAPIData?.request?.permits, setSelectedSpecies, speciesData]
    );

    // Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      updateSectionProps(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSectionProps]);

    // Only sync species from server when requestAPIData changes
    useEffect(() => {
      if (prevRequestAPIDataRef.current !== requestAPIData) {
        const species = requestAPIData?.request?.permits?.[0]?.species;
        if (species) {
          setSpeciesData(species);
          setSpeciesDataIsDirty(false);
        }
        prevRequestAPIDataRef.current = requestAPIData;
      }
    }, [requestAPIData]);

    // Only set selected species from database on initial load
    useEffect(() => {
      if (speciesFromDb && permitData?.species) {
        const checkedSpecies = speciesFromDb.filter((species) =>
          permitData.species.some((dataSpecies) => dataSpecies.commonName === species)
        );
        setSelectedSpecies(checkedSpecies);
      }
    }, [speciesFromDb, permitData?.species, setSelectedSpecies]);

    useEffect(() => {
      updateSection(stepNo, { valid: isReadOnly ? true : isValid });
    }, [isValid, updateSection, stepNo, isReadOnly, isDirty]); // isDirty is needed to properly trigger validity for new requests (since errors are not added to object until dirty)

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}

        <StepHeader className='margin-bottom-3' text='Other Laws and Regulations' />

        <div className='margin-left-1'>
          <Section label='Endangered and/or Threatened Species'>
            <p className='text-bold'>
              The Endangered Species Act (ESA), (16 USC 1531-1544), amended 1988, establishes a National program for the
              conservation of threatened and endangered species of fish, wildlife, and plants and the habitat upon which
              they depend. Section 7(a) of the ESA requires Federal agencies (including the USACE) to consult with the
              U.S. Fish and Wildlife Service (USFWS) to ensure that their actions are not likely to jeopardize the
              continued existence of endangered or threatened species or adversely modify or destroy their critical
              habitats. Section 7(c) of the ESA and the Federal regulations on endangered species coordination (50 CFR §
              402.12) require that Federal agencies prepare biological assessments of the potential effects of major
              actions on listed species and critical habitat.
            </p>
            <p className='text-bold margin-bottom-3'>
              The USACE evaluates permit applications on a case-by-case basis to determine a project's potential to
              affect threatened and endangered species.
            </p>

            <h4>USFWS IPAC Species List</h4>

            <fieldset id='ipac-species-list padding-0'>
              <Label htmlFor='ipac-species-list'>
                I believe that the following species and/or its habitat may occur within my project area. (leave
                unchecked if you're not sure):
              </Label>
              <IPACSpeciesTable
                fetchSpecies={fetchSpecies}
                isLoading={isLoading}
                isError={isError}
                handleSpeciesCheck={handleSpeciesCheck}
                endangeredSpeciesList={endangeredSpeciesList}
                selectedSpecies={selectedSpecies}
              />
            </fieldset>

            <Alert type='warning' heading='IMPORTANT' headingLevel='h6'>
              The IPAC species list only covers species listed under the Endangered Species Act that are managed by the
              United States Fish and Wildlife Service. Please consult with the{' '}
              <ExternalLink
                href='https://noaa.maps.arcgis.com/apps/webappviewer/index.html?id=68d8df16b39c48fe9f60640692d0e318'
                content='National Marine Fisheries Service (NMFS) Critical Habitat'
              />{' '}
              AND the applicable NMFS "Regional Mapper" for NMFS listed species.
              <p>
                {text[isGpRequest]['endangeredSpeciesAlert'].label}{' '}
                {text[isGpRequest]['endangeredSpeciesAlert'].tooltipText}
              </p>
            </Alert>
            <TextAreaInput
              name='affectedSpecies'
              onChange={handleChange}
              maxLength={4000}
              label={text[isGpRequest]['affectedSpecies'].label}
              tooltip={text[isGpRequest]['affectedSpecies'].tooltipText}
              readOnly={isReadOnly}
              required
            />
          </Section>

          <Section label='Historic Properties'>
            <p className='text-bold'>
              Section 106 of the National Historic Preservation Act of 1966 (NHPA), as amended, requires Federal
              agencies to consider the effects of their undertakings on Historic Properties and afford the Advisory
              Council on Historic Preservation a reasonable opportunity to comment on such undertakings. Therefore,
              prior to the issuance or authorization of any permit under Section 404 of the Clean Water Act or Section
              10 of the Rivers and Harbors Act, the USACE must consider the effect the permit may have on Historic
              Properties. Historic Properties may include prehistoric or historic districts, sites, buildings,
              structures, objects, sacred sites, and traditional cultural places that are included in, or eligible for
              inclusion in, the National Register of Historic Places.{' '}
            </p>
            <p className='text-bold'>
              The State/Territory or Tribal Historic/Cultural preservation organization in your jurisdiction may be able
              to provide you with resources on how to determine whether your project may have the potential to affect a
              historic/cultural resource.{' '}
            </p>

            <div>
              <TextAreaInput
                name='impactedHistoricalProperty'
                onChange={handleChange}
                required
                placeholder='List Historic Property'
                maxLength={4000}
                label={text[isGpRequest]['impactedHistoricalProperty'].label}
                tooltip={text[isGpRequest]['impactedHistoricalProperty'].tooltipText}
                readOnly={isReadOnly}
              />
            </div>
          </Section>

          <Section label='National Wild and Scenic Rivers'>
            <p className='text-bold'>
              No activity may occur in a component of the National Wild and Scenic River System, or in a river
              officially designated by Congress as a study river for possible inclusion in the system while the river is
              in an official study status, unless the appropriate Federal agency with direct management responsibility
              for such river, has determined in writing that the proposed activity will not adversely affect the Wild
              and Scenic River designation or study status
              {!isGpRequest && ' (Nationwide Permit General Condition 16)'}.
            </p>
            <p className='text-bold'>
              Information on Wild and Scenic Rivers may be obtained from the appropriate Federal land management agency
              responsible for the designated Wild and Scenic River or Study River (e.g., National Park Service, U.S.
              Forest Service, Bureau of Land Management, U.S. Fish and Wildlife Service).
            </p>
            <ExternalLink
              href='https://rivers.gov'
              title='Rivers.gov'
              content='National Wild and Scenic Rivers System Resource'
            />

            <div>
              <SelectInput
                className='width-50'
                fullWidth={false}
                label={text[isGpRequest]['isWildScenicRiver'].label}
                name='isWildScenicRiver'
                required
                tooltip={text[isGpRequest]['isWildScenicRiver'].tooltipText}
                readOnly={isReadOnly}
                onChange={handleChange}
              >
                <option key='2' value='Yes'>
                  Yes
                </option>
                <option key='3' value='No'>
                  No
                </option>
                <option key='4' value={'I am not sure'}>
                  I'm not sure
                </option>
              </SelectInput>
            </div>

            {nationalRiver === 'Yes' && (
              <div>
                <TextAreaInput
                  name='wildScenicRiverName'
                  onChange={handleChange}
                  required
                  placeholder='Scenic River List'
                  maxLength={4000}
                  label=' List National Wild and Scenic River or Study River'
                  tooltipClickable
                  tooltip={text[isGpRequest]['wildScenicRiverName'].tooltipText}
                  readOnly={isReadOnly}
                />
              </div>
            )}
          </Section>

          <Section label='USACE Civil Work Projects (Section 408)'>
            {isGpRequest ? (
              <p className='text-bold'>
                If a Nationwide Permit activity also requires review by, or permission from, the USACE pursuant to 33
                U.S.C. 408
                <Tooltip clickable name='section-408' content={text.section408.tooltipText} /> because it will alter or
                temporarily or permanently occupy or use a U.S. Army Corps of Engineers (USACE) federally authorized
                Civil Works project (a "USACE project"), the prospective permittee must submit a pre-construction
                notification. An activity that requires Section 408
                <Tooltip clickable name='section-408' content={text.section408.tooltipText} /> permission and/or review
                is not authorized by a Nationwide Permit until the appropriate USACE office issues the Section 408
                <Tooltip clickable name='section-408' content={text.section408.tooltipText} /> permission or completes
                its review to alter, occupy, or use the USACE project, and the district engineer issues a written
                Nationwide Permit verification.{' '}
              </p>
            ) : (
              <p className='text-bold'>
                An activity that requires Section 408
                <Tooltip clickable name='section-408' content={text.section408.tooltipText} /> permission and/or review
                is not authorized until the appropriate USACE office issues the Section 408 permission or completes its
                review to alter, occupy, or use the USACE project.
              </p>
            )}

            <div>
              <SelectInput
                className='width-50'
                fullWidth={false}
                label={text[isGpRequest]['isS408PermissionRequired'].label}
                name='isS408PermissionRequired'
                required
                tooltip={text[isGpRequest]['isS408PermissionRequired'].tooltipText}
                readOnly={isReadOnly}
                onChange={handleChange}
              >
                <option key='2' value='Yes'>
                  Yes
                </option>
                <option key='3' value='No'>
                  No
                </option>
                <option key='4' value={'I am not sure'}>
                  I'm not sure
                </option>
              </SelectInput>
            </div>

            {civilWorkSelect === 'Yes' && (
              <div>
                <SelectInput
                  className='width-50'
                  fullWidth={false}
                  label='Have you submitted a written request for Section 408 permission from the USACE District having jurisdiction over that project?'
                  name='isS408RequestSubmitted'
                  required
                  tooltip=''
                  readOnly={isReadOnly}
                  onChange={handleChange}
                >
                  <option key='2' value='true'>
                    Yes
                  </option>
                  <option key='3' value='false'>
                    No
                  </option>
                </SelectInput>
              </div>
            )}
            {(submittedRequest === 'false' || submittedRequest === false) && (
              <p>
                Please contact the location's District 408 coordinator. See:{' '}
                <ExternalLink
                  href='https://www.usace.army.mil/Missions/Civil-Works/Section408'
                  title='Section 408'
                  content='https://www.usace.army.mil/Missions/Civil-Works/Section408'
                />
                .
              </p>
            )}
            {(submittedRequest === 'true' || submittedRequest === true) && civilWorkSelect === 'Yes' && (
              <div>
                <p>Provide the date your request was submitted to the USACE District: </p>
                <DatePicker
                  className='width-50'
                  name='s408RequestSubmittedDate'
                  label='Submission Date'
                  onChange={handleChange}
                  min={dateBeforeA}
                  readOnly={isReadOnly}
                  required
                />
              </div>
            )}
          </Section>
        </div>
      </FormProvider>
    );
  }
);
PermitsFormOtherLawsRegulations.metadata = PermitsFormOtherLawsRegulationsMetadata;
export default PermitsFormOtherLawsRegulations;
